import { ImportSessionStorage } from 'utils/importSessionStorage';

/**
 * Discord sign-in and the server-join opt-in (Decision 4, ruling E10). Both are
 * full-page navigations to the API host; the returnTo rides in the backend's
 * signed OAuth state and comes back on /play/auth/discord/complete.
 */

const apiBaseUrl = (): string => {
  const raw = process.env.REACT_APP_API_URL;
  if (raw && /^https?:\/\//.test(raw)) return raw.replace(/\/$/, '');
  return 'http://localhost:4000/api/v1';
};

/** Only relative paths under /play may be used as a return target. */
export const safePlayReturnTo = (value: string | null | undefined, fallback = '/play'): string => {
  if (!value) return fallback;
  if (!value.startsWith('/play') || value.startsWith('//')) return fallback;
  if (/[\r\n]/.test(value)) return fallback;
  return value;
};

export const discordStartUrl = (returnTo: string): string =>
  `${apiBaseUrl()}/auth/discord/start?returnTo=${encodeURIComponent(safePlayReturnTo(returnTo))}`;

export const discordJoinServerUrl = (returnTo: string, startPath = '/auth/discord/join-server'): string => {
  const path = startPath.replace(/^\/api\/v1/, '');
  return `${apiBaseUrl()}${path}?returnTo=${encodeURIComponent(safePlayReturnTo(returnTo))}`;
};

export type DiscordStartResult = 'redirecting' | 'unavailable' | 'error';

/**
 * The start endpoints 302 to Discord when an app is configured and answer
 * 503 { code: DISCORD_NOT_CONFIGURED } when none is. A full-page navigation
 * into a failure would show raw JSON or a browser error page, so probe first
 * with a manual-redirect fetch: an opaque redirect means go; a 404, any 5xx
 * or a network failure means Discord is unavailable (Eric's ruling, inline
 * copy under the button); 401 and 409 are a sign-in problem worth retrying.
 */
const probeThenNavigate = async (url: string): Promise<DiscordStartResult> => {
  try {
    const res = await fetch(url, { credentials: 'include', redirect: 'manual' });
    if (res.type === 'opaqueredirect' || res.status === 0 || res.ok) {
      window.location.assign(url);
      return 'redirecting';
    }
    if (res.status === 404 || res.status >= 500) return 'unavailable';
    if (res.status === 409 || res.status === 401) return 'error';
  } catch {
    return 'unavailable';
  }
  window.location.assign(url);
  return 'redirecting';
};

/**
 * A start that did not leave the page must not leave a return address behind
 * (Asana 1220218671326301, Decision 2): a stale one would redirect a later,
 * unrelated sign-in to Play.
 */
const clearUnlessRedirecting = (result: DiscordStartResult): DiscordStartResult => {
  if (result !== 'redirecting') ImportSessionStorage.clearReturnTo();
  return result;
};

/** Leaves the app for Discord. Also stores the sessionStorage returnTo as a belt. */
export const beginDiscordLogin = async (returnTo: string): Promise<DiscordStartResult> => {
  const target = safePlayReturnTo(returnTo);
  ImportSessionStorage.setReturnTo(target);
  return clearUnlessRedirecting(await probeThenNavigate(discordStartUrl(target)));
};

export const beginDiscordServerJoin = async (returnTo: string, startPath?: string): Promise<DiscordStartResult> =>
  clearUnlessRedirecting(await probeThenNavigate(discordJoinServerUrl(returnTo, startPath)));
